/**
 * Page Resume Integration for Dealer/Franchisee
 *
 * Wires up the PageResumeHandler with existing app infrastructure:
 * - Auth: Token refresh and validation
 * - Realtime: SSE reconnection
 * - Locks: Resource lock recovery
 * - Data: User data store refresh
 *
 * This provides a centralized, ordered recovery pipeline for:
 * - Tab resume / visibility change
 * - Browser wake from sleep
 * - Network reconnection
 * - Manual triggers (e.g., from 401/412 interceptors)
 */

import { getCurrentUser, setCurrentUser } from '../../../webmodule-common/other/api/current-user';
import {
  pageResumeHandler,
  ResumeCancelType,
  ResumeContext,
  ResumeResult,
  ResumeStage
} from '../../../webmodule-common/other/page-resume-handler';
import { refreshUserToken } from '../../../webmodule-common/other/api/dealer-api-communications';
import { userDataStore } from './current-user-data-store';
import { WMEventSourceClient } from '../../../webmodule-common/other/general/event-source-client';

// Track if we're in the middle of a resume to prevent cascading dialogs
let isResuming = false;

/**
 * Check if a resume operation is currently in progress.
 * Use this to suppress error dialogs that would cascade on top of resume handling.
 */
export function isResumeInProgress(): boolean {
  return isResuming;
}

/**
 * Auth Stage: Verify token is valid and refresh if needed.
 * Cancels hard (logout) if token refresh fails for an authenticated user.
 */
async function authStageHook(ctx: ResumeContext): Promise<void> {
  const user = getCurrentUser();
  if (!user) {
    // No user logged in - nothing to resume, silently skip
    // This is NOT an error - the user may be on the login page
    console.log('[PageResume] No user - skipping resume');
    return;
  }

  // Check if token needs refresh (within 1 hour of expiry)
  const refreshedUser = await refreshUserToken();
  if (!refreshedUser) {
    // Token refresh failed - user session is invalid (e.g., 412 duplicate login)
    // Only cancel hard if we HAD a user but now the session is invalid
    ctx.cancel(ResumeCancelType.Hard, 'Token refresh failed');
    return;
  }

  console.log('[PageResume] Auth stage passed');
}

/**
 * Realtime Stage: Ensure SSE connection is active.
 * The SSE client handles its own reconnection, but we ensure it's triggered.
 */
async function realtimeStageHook(ctx: ResumeContext): Promise<void> {
  if (ctx.abortSignal.aborted) return;

  const user = getCurrentUser();
  if (!user) return;

  try {
    // WMEventSourceClient handles reconnection internally via its own error handler.
    // The SSE connection state drives GlobalNotificationManager.online which
    // triggers the existing recovery flows (lock recovery, data refresh).
    // We don't need to manually trigger reconnection here - the SSE client
    // will reconnect automatically when it detects the connection is lost.
    //
    // If we're in a reconnecting state, we could wait for it to complete,
    // but that could block the resume pipeline. Better to let it happen async.
    const client = WMEventSourceClient.getInstance();
    const state = client.reconnectionState;
    if (state.isReconnecting) {
      console.log('[PageResume] SSE is reconnecting (attempt', state.attemptCount, ')');
    }
  } catch (e) {
    // SSE reconnection failure is not fatal - continue with other stages
    console.warn('[PageResume] SSE check error:', e);
  }

  console.log('[PageResume] Realtime stage passed');
}

/**
 * Locks Stage: Verify resource locks are still valid.
 * This is handled by the existing lock recovery system in resource-lock.ts
 * which listens to GlobalNotificationManager.onConnectionStateChange.
 *
 * The PageResumeHandler triggers before that, so locks will be checked
 * after SSE reconnects and fires the online event.
 */
async function locksStageHook(ctx: ResumeContext): Promise<void> {
  if (ctx.abortSignal.aborted) return;

  // Lock recovery is triggered by GlobalNotificationManager.onConnectionStateChange
  // which fires when SSE reconnects. We don't need to duplicate that logic here.
  // However, we could add explicit lock verification if needed.

  console.log('[PageResume] Locks stage passed (deferred to SSE reconnect)');
}

/**
 * Data Stage: Refresh user data store.
 */
async function dataStageHook(ctx: ResumeContext): Promise<void> {
  if (ctx.abortSignal.aborted) return;

  const user = getCurrentUser();
  if (!user) return;

  try {
    userDataStore.requestRefresh();
    await userDataStore.loadCoreDetails();
    console.log('[PageResume] Data stage passed');
  } catch (e) {
    // If user was logged out during data refresh, don't treat as error
    if (!getCurrentUser()) {
      ctx.cancel(ResumeCancelType.Hard, 'User logged out during data refresh');
      return;
    }
    console.error('[PageResume] Data refresh error:', e);
    // Continue - data refresh failure is not necessarily fatal
  }
}

/**
 * Initialize the page resume handler with all stage hooks.
 * Call this once during application initialization.
 */
export function initPageResumeHandler(): void {
  // Register stage hooks
  pageResumeHandler.register(ResumeStage.Auth, authStageHook);
  pageResumeHandler.register(ResumeStage.Realtime, realtimeStageHook);
  pageResumeHandler.register(ResumeStage.Locks, locksStageHook);
  pageResumeHandler.register(ResumeStage.Data, dataStageHook);

  // Handle hard cancel (logout)
  pageResumeHandler.onHardCancel = async reason => {
    console.log('[PageResume] Hard cancel - logging out:', reason);
    await setCurrentUser(null);
  };

  // Handle workflow cancel (exit current editor)
  pageResumeHandler.onWorkflowCancel = reason => {
    console.log('[PageResume] Workflow cancel:', reason);
    // The lock system handles this via onLockRevoked callbacks
  };

  // Track resume state for cascading dialog prevention
  const originalTrigger = pageResumeHandler.trigger.bind(pageResumeHandler);
  pageResumeHandler.trigger = async metadata => {
    isResuming = true;
    try {
      return await originalTrigger(metadata);
    } finally {
      isResuming = false;
    }
  };

  // Start listening to browser lifecycle events
  pageResumeHandler.start();

  console.log('[PageResume] Handler initialized');
}
